import { derived, get, writable } from 'svelte/store';
import {
	BillingPackageName,
	BillingSanctionLevel,
	type BillingPackageGroupPrice,
	type BillingPaymentMethod,
	type BillingVat,
	type Contract,
	type UpdateBillingInfoRequest,
	apiAppBillingContractApplyvoucher,
	apiAppBillingContractCancelsubscription,
	apiAppBillingContractGetcontract,
	apiAppBillingContractGettaxes,
	apiAppBillingContractInfo,
	apiAppBillingContractListallprices,
	apiAppBillingContractReactivatesubscription,
	apiAppBillingPaymentListpaymentmethods
} from '#lib/types/generated/index.js';
import { handleApiError } from './api';
import { FormStatus, formStatus } from './form';

export function createDefaultContract(): Contract {
	return {
		subscriptionAmount: 0,
		permanentDiscountAmount: 0,
		permanentDiscountPercent: 0,
		currency: 'EUR',
		frequency: 1,
		billingEnabled: false,
		nextInvoiceDate: null,
		nextInvoiceAdditionalLines: [],
		nextInvoiceDiscountAmount: 0,
		nextInvoiceDiscountPercent: 0,
		sanctionLevel: BillingSanctionLevel.Zero,
		sanctionLevelDegradeDate: null,
		canUserChangePackage: false,
		nextPackage: BillingPackageName.Basic,
		currentPackage: BillingPackageName.Basic,
		recoveryFee: 0,
		recoverAgreedAmount: 0,
		totalAmountUnpaid: 0,
		cancelAtPeriodEnd: false,
		paymentFeePercent: 0,
		paymentFeeFixed: 0,
		guaranteeFee: 0,
		splitPaymentFee: 0,
		vatNumber: null,
		vatPercent: 20,
		legalCompanyIdentifier: null,
		companyName: '',
		companyAddress: '',
		city: null,
		zipCode: null,
		country: 'FR',
		qontoCustomerId: null,
		requirePaymentMethod: false,
		hasOpenInvoice: false,
		hasProcessingPayment: false
	};
}

export const contractStore = writable<Contract>(createDefaultContract());
export const paymentMethodStore = writable<BillingPaymentMethod | null | undefined>();
export const priceStore = writable<BillingPackageGroupPrice[]>([]);
export const showBillingPopins = writable<boolean>(false);
export const billingOnboardingInProgress = writable<boolean>(false);

export const isContractValid = derived(contractStore, ($contractStore) => {
	return $contractStore.sanctionLevel <= 1 && $contractStore.billingEnabled;
});

export const missingPaymentMethod = derived(
	[contractStore, paymentMethodStore],
	([$contractStore, $paymentMethodStore]) => {
		return Boolean($contractStore.requirePaymentMethod && $paymentMethodStore === null);
	}
);

export const missingContractInfo = derived(
	[contractStore, missingPaymentMethod],
	([$contractStore, $missingPaymentMethod]) => {
		return !$contractStore.qontoCustomerId || $missingPaymentMethod;
	}
);

export async function applyVoucher(voucher: string): Promise<Contract> {
	const hydrated = await apiAppBillingContractApplyvoucher({ voucher });
	contractStore.set(hydrated);
	return hydrated;
}

export async function updateContractInfo(data: UpdateBillingInfoRequest): Promise<Contract> {
	const hydrated = await apiAppBillingContractInfo(data);
	contractStore.set(hydrated);
	return hydrated;
}

export function cancelSubscription(): void {
	apiAppBillingContractCancelsubscription().then((contract) => {
		contractStore.set(contract);
	});
}

export function reactivateSubscription(): void {
	formStatus.set(FormStatus.Processing);
	apiAppBillingContractReactivatesubscription().then((contract) => {
		formStatus.set(FormStatus.Success);
		contractStore.set(contract);
	});
}

export function loadContract(forceReload = false): void {
	if (forceReload || !get(contractStore).companyName) {
		apiAppBillingContractGetcontract().then((contract) => {
			contractStore.set(contract);
		});
	}
}

export function loadPrices(forceReload = false): void {
	if (forceReload || get(priceStore).length === 0) {
		apiAppBillingContractListallprices().then((res) => {
			priceStore.set(
				res.items.sort((a, b) => (a.prices[0]?.amount ?? 0) - (b.prices[0]?.amount ?? 0))
			);
		});
	}
}

export const billingTaxes = writable<BillingVat[]>([]);

export function refreshPaymentMethod(): void {
	apiAppBillingPaymentListpaymentmethods()
		.then((res) => {
			paymentMethodStore.set(res.items[0] ?? null);
		})
		.catch((error) => {
			handleApiError(error, (err) => {
				if (err.httpCode === 422) {
					console.error('Stripe API error loading payment method:', err.error);
					paymentMethodStore.set(undefined);
					return;
				}
				paymentMethodStore.set(null);
			});
		});
}

export async function fetchBillingTaxes(): Promise<BillingVat[]> {
	const res = await apiAppBillingContractGettaxes();
	billingTaxes.set(res.items);
	return res.items;
}
